// context/themeContext.jsx
import { createContext, useContext, useEffect, useState } from "react"; 
import {  postTheme ,patchTheme ,deleteTheme ,getThemeActive} from '../services/ThemeServices';
// ─── Theme por defecto (igual a tu SCSS) ─────────────────────────────────────
export const DEFAULT_THEME = {
  accent:         "#e94560",
  accent_hover:   "#d63851",
  accent_soft:    "rgba(233,69,96,0.08)",
  primary_color:  "#1a1a2e",
  primary_light:  "#16213e",
  bg_main:        "#f8f9fc",
  bg_card:        "#ffffff",
  bg_elevated:    "#ffffff",
  bg_subtle:      "#f1f3f8",
  text_primary:   "#1a1a2e",
  text_secondary: "#5a5f7a",
  text_muted:     "#8e92a4",
  text_inverse:   "#ffffff",
  border_color:   "#e4e7f0",
  border_hover:   "#c8ccd8",
  divider:        "#eef0f6",
  success_color:  "#10b981",
  warning_color:  "#f59e0b",
  danger_color:   "#ef4444",
  info_color:     "#3b82f6",
  purple_color:   "#7c3aed",
  font_heading:   "'Plus Jakarta Sans', sans-serif",
  font_body:      "'DM Sans', sans-serif",
  font_mono:      "'Space Grotesk', monospace",
  radius_sm:      "8px",
  radius_md:      "12px",
  radius_lg:      "16px",
  radius_xl:      "20px",
  footer_copyright: "",
  footer_address:   "",
};

// ─── Mapeo DB snake_case → CSS variable ──────────────────────────────────────
const DB_TO_CSS = {
  accent:         "--accent",
  accent_hover:   "--accent-hover",
  accent_soft:    "--accent-soft",
  primary_color:  "--primary",
  primary_light:  "--primary-light",
  bg_main:        "--bg-main",
  bg_card:        "--bg-card",
  bg_elevated:    "--bg-elevated",
  bg_subtle:      "--bg-subtle",
  text_primary:   "--text-primary",
  text_secondary: "--text-secondary",
  text_muted:     "--text-muted",
  text_inverse:   "--text-inverse",
  border_color:   "--border",
  border_hover:   "--border-hover",
  divider:        "--divider",
  success_color:  "--success",
  warning_color:  "--warning",
  danger_color:   "--danger",
  info_color:     "--info",
  purple_color:   "--purple",
  font_heading:   "--font-heading",
  font_body:      "--font-body",
  font_mono:      "--font-mono",
  radius_sm:      "--radius-sm",
  radius_md:      "--radius-md",
  radius_lg:      "--radius-lg",
  radius_xl:      "--radius-xl",
};

// ─── Aplica las variables al :root ────────────────────────────────────────────
const applyTheme = (theme) => {
  if (!theme) return;
  const root = document.documentElement;
  Object.entries(DB_TO_CSS).forEach(([field, cssVar]) => {
    if (theme[field]) root.style.setProperty(cssVar, theme[field]);
  });
  // Recalcular shadow-glow desde el accent
  if (theme.accent && theme.accent.startsWith("#")) {
    const hex = theme.accent.replace("#", "");
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      root.style.setProperty("--shadow-glow", `0 0 24px rgba(${r},${g},${b},0.15)`);
    }
  }
};

// ─── Context ──────────────────────────────────────────────────────────────────
export const ThemeContext = createContext({});

export const ThemeProvider = ({ children }) => {
  const [themeConfig, setThemeConfig] = useState(DEFAULT_THEME);
  const [themeId,     setThemeId]     = useState(null);
  const [loading,     setLoading]     = useState(true);

  // ── Cargar theme activo al montar ──────────────────────────────────────────
  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const data = await getThemeActive() 
        if (data) {
          setThemeConfig(data);
          setThemeId(data.id);
          applyTheme(data);
        }
      } catch (e) {
        console.warn("ThemeContext: usando theme por defecto.", e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchTheme();
  }, []);

  // ── saveTheme: aplica en tiempo real Y guarda en la DB ────────────────────
  const saveTheme = async (next) => {
    setThemeConfig(next);
    applyTheme(next);
    if (themeId) {
      try {
        await patchTheme(themeId, next);
      } catch (e) {
        console.error("Error al guardar theme en DB:", e.message);
      }
    }
  };

  // ── resetTheme: vuelve al DEFAULT y lo guarda ─────────────────────────────
  const resetTheme = () => {
    setThemeConfig(DEFAULT_THEME);
    applyTheme(DEFAULT_THEME);
    if (themeId) {
       patchTheme(themeId, DEFAULT_THEME).catch(console.error);
    }
  };

  // ── exportTheme: descarga el theme como JSON ──────────────────────────────
  const exportTheme = () => {
    const blob = new Blob([JSON.stringify(themeConfig, null, 2)], { type: "application/json" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = "theme.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── importTheme: carga un JSON y lo aplica ────────────────────────────────
  const importTheme = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      setThemeConfig(parsed);
      applyTheme(parsed);
      if (themeId) {
        patchTheme(themeId, parsed).catch(console.error);
      }
      return true;
    } catch {
      return false;
    }
  };

  // ── previewTheme: aplica visualmente sin guardar (para live preview) ──────
  const previewTheme = (data) => applyTheme(data);

  // ── resetPreview: restaura el theme guardado ──────────────────────────────
  const resetPreview = () => applyTheme(themeConfig);

  return (
    <ThemeContext.Provider value={{
      themeConfig, loading, themeId,
      saveTheme, resetTheme,
      exportTheme, importTheme,
      previewTheme, resetPreview,
      DEFAULT_THEME,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);